import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Input,
    Popover,
    Radio,
    Select,
    Tooltip,
} from 'antd';
import jaJP from 'antd/lib/date-picker/locale/ja_JP';
import { RadioChangeEvent } from 'antd/lib/radio';
import { UploadFile } from 'antd/lib/upload/interface';

import BaseForm from 'components/modules/BaseForm';
import BaseModal from 'components/modules/BaseModal';
import CustomRangePicker from 'components/modules/CustomRangePicker';
import {
    allowedExtensions,
    FileUploader,
    FileUploaderHandler,
} from 'components/modules/FileUploader';
import Spin from 'components/modules/Spin';
import {
    DatePickerRangesIncludeInfinite,
    formRules,
    getDisabledDate,
    getMoment,
    validations,
} from 'constants/GlobalConfig';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

import { modalBaseLayoutWithoutLabel, modalBaseLayout } from '../AppTemplate/EditAppTemplate';
import { EditUrlAndParam } from '../AppTemplate/Forms/EditUrlAndParam';
import { createInitialValues } from '../AppTemplate/Forms/editUrlAndParamUtils';
import AutoCompleteUrl from './AutoCompleteUrl';
import { source as AutoCompleteUrlSource } from './AutoCompleteUrl/source';
import { findCandidate } from './utils/findCandidate';

import './EditApp.scss';
import { useAppView } from './hooks/useAppView';
import { checkFormChange, convertDateToString } from 'utils/Helper';
import { IconCandidate } from './common/IconCandidate';

const Form = BaseForm.ModalForm;
const { statusEnum } = Actions;
const { Option } = Select;

const moment = getMoment();

type File = UploadFile & { uuid?: string };

type AppTemplate = Http.Connection.Response.AppTemplate.Search['result']['items'][0];
type App = Http.Connection.Response.Apps.View['result'];
type AppCategory = App.AppsCategory & { use_deep_linking: boolean | null };

type Props = {
    applications: App[];
    category: AppCategory;
    onCancel: () => void;
    onOk: () => void;
    record: App | null;
    shouldCreate: boolean;
    useDeepLinking: boolean;
    visible: boolean;
};

type FormValues = {
    description: string;
    duration: [moment.Moment, moment.Moment | undefined];
    files: File[];
    isCooperationUrl: boolean;
    open_new_window: boolean;
    title: string;
    url: string;
    params: FormParams[];
    ltiUrlCandidateUuid: string;
    ltiUrlUrl: string;
    ltiUrlKey: string;
    ltiUrlSecret: string;
    control_application_template_uuid?: string;
    param_json?: {
        client_id: string
        deployment_id: string
        initiate_login_url: string
        is_send_user_info: boolean
        login_hint: string
        public_keyset: string
        redirect_uris: string[]
        tool_url: string
    };
    enableTwoFactorAuth: boolean;
};

type FormParams = {
    ltiParamCandidateUuid: string | undefined
    ltiParamUuid: string
    ltiParamValue: string
}

/**
 * アプリの編集
 * - 全体管理者から配信されたアプリ(is_published_item=true)の場合は、各項目は変更できない
 */
const EditApp: React.FC<Props> = ({
    applications,
    category,
    onCancel,
    onOk,
    record,
    shouldCreate,
    useDeepLinking,
    visible,
}) => {
    const [form] = Form.useForm<FormValues>();
    const autoCompleteUrl = Form.useWatch('url', form);
    const dispatch = useDispatch();
    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);
    const isEnableTenantMFA = useAppSelector(state => state.isEnableTenantMFA.payload);
    const appsEditConnection = useAppSelector(state => state.appsEditConnection);
    const appsTemplateSearchConnection = useAppSelector(state => state.appsTemplateSearchConnection);
    const [appView, viewGet,] = useAppView();
    const [initSectionNum, setInitSectionNum] = useState(0);// 0:初期, 1:アプリテンプレート取得中, 2:view取得中, 3:完了
    const [editLoading, setEditLoading] = useState(false);
    const [searchTemplateLoading, setSearchTemplateLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isCooperationUrl, setIsCooperationUrl] = useState(true);
    const [selectAppTemplate, setSelectAppTemplate] = useState<AppTemplate | null>(null);
    const [selectedPresetFlags, setSelectedPresetFlags] = useState<{ files: boolean, url: boolean } | undefined>(undefined);
    const [isExistConnectedApp, setIsExistConnectedApp] = useState(false);
    const [isActiveForApp, setIsActiveForApp] = useState(false);
    const [appTemplates, setAppTemplates] = useState<AppTemplate[]>([]);
    const [formInitValue, setFormInitValue] = useState<any>({});
    const [formChangeValue, setFormChangeValue] = useState<any>({});
    const [isDisabledSubmitBtn, setIsDisabledSubmitBtn] = useState(false);
    const [isSelectDefaultImage, setIsSelectDefaultImage] = useState<boolean>(false);
    const disabledForPublishedItem = !!(record && 'is_published_item' in record && record.is_published_item);
    const fileUploaderRef = useRef<FileUploaderHandler>(null);

    const isCreate = !record;
    const isUpdate = !isCreate;
    const isAutoCompleteUrl = AutoCompleteUrlSource.filter(
        e => e.url === record?.url && 'files' in record && e.image.name === record?.files?.[0]?.file_name
    ).length > 0;

    const isEditable = !(isUpdate && !isCooperationUrl) || (isExistConnectedApp && isActiveForApp);

    useEffect(() => {// 開閉時の処理（open時は初期値セット,close時は初期化
        if (!visible) {// 検索状態を表す数値を初期状態に
            setInitSectionNum(0);
            return;
        }
        form.resetFields();
        // 初期値のセットなど
        const values = {
            description: '',
            duration: [
                // eslint-disable-next-line sort-keys
                moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
                undefined,
            ],
            files: [],
            isCooperationUrl: true,
            open_new_window: true,
            enableTwoFactorAuth: false,
        };
        setSelectAppTemplate(null);
        setIsCooperationUrl(values.isCooperationUrl);
        setSelectedPresetFlags(undefined);
        form.setFieldsValue(values);

        // アプリテンプレートの取得
        dispatch(Actions.http.connection.apps.template.search({ page_size: 500, use_deep_linking: 0 }, 1));
        setInitSectionNum(1);
        setSearchTemplateLoading(true);
        if (isUpdate) setDetailLoading(true);// 編集の時は設定済みの内容を取得するのでloading=trueに

    }, [record, visible]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {// アプリテンプレート取得後のアプリ詳細取得
        if (initSectionNum !== 1) return;
        const { meta, payload } = appsTemplateSearchConnection;

        setSearchTemplateLoading(meta.fetch);
        if (meta.status !== statusEnum.SUCCESS) return;
        if (!payload.result?.items) return;

        setAppTemplates(payload.result.items);

        // 編集の場合は設定済みの内容を取得
        if (isUpdate) {
            viewGet(record.uuid)
            setInitSectionNum(2);
        } else {
            setInitSectionNum(3);
        };
    }, [initSectionNum, appsTemplateSearchConnection]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {// 編集時に設定済みの内容を取得した際の処理
        setDetailLoading(appView.isLoading);

        if (initSectionNum !== 2) return; // appviewを取得しに行っていない場合抜ける
        if (appView.isLoading) return;
        if (!appView.result) {// 取得しに行ったが何も取れなかった時は抜ける
            setInitSectionNum(3);
            return;
        }

        const useDeepLinking = category?.use_deep_linking ?? false;
        const newRecord = appView.result;
        const { url = '', ...rest } = newRecord;
        const params = 'params' in newRecord ? newRecord.params : [];
        const ltiAppTemplate = 'lti_app_template' in newRecord ? newRecord.lti_app_template : null;
        const ltiUrl = 'lti_url' in newRecord ? newRecord.lti_url : null;
        const paramJson = ltiUrl?.param_json;
        const lti11Values = {
            ltiUrlKey: ltiUrl?.key ?? '',
            ltiUrlSecret: ltiUrl?.secret ?? '',
            ltiUrlUrl: ltiUrl?.url ?? '',
        };

        const value = {
            ...rest,
            ...lti11Values,
            control_application_template_uuid: ltiAppTemplate?.uuid,
            duration: [moment.unix(newRecord.publish_start_at), moment.unix(newRecord.publish_end_at)],
            files: newRecord.files,
            isCooperationUrl: useDeepLinking ? true : ltiAppTemplate === null,
            ltiUrlCandidateUuid: findCandidate(newRecord)?.uuid,
            param_json: paramJson ?
                {
                    ...paramJson,
                    deployment_id: paramJson?.deployment_id ?? '',
                } :
                undefined,
            params: params.map(param => ({
                ltiParamCandidateUuid: param.candidates?.find(c => c.is_default)?.uuid,
                ltiParamUuid: param.uuid,
                ltiParamValue: param.value,
            })),
            url: url ?? undefined,
            enableTwoFactorAuth: newRecord.enable_two_factor_auth ?? false
        };
        form.setFieldsValue(value);
        setSelectAppTemplate(getAppTemplateWithDefaultValue(newRecord));
        setIsCooperationUrl(value.isCooperationUrl);

        setInitSectionNum(3);
    }, [appView]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {// 登録処理実行後の処理（loading, onOkの実行)
        setEditLoading(appsEditConnection.meta.fetch);
        if (appsEditConnection.meta.status === statusEnum.SUCCESS) {// 作成完了
            onOk();
        }
    }, [appsEditConnection]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {//isExistConnectedApp(lti接続するアプリがいるか)、isActiveForApp(lti接続するアプリがアクティブか)
        // modalを開くタイミングでsetIsExistConnectedAppが行われておらず、
        // 初期値のfalseとなってしまっているため visible をトリガーで行う
        if (!visible) return;

        const ltiAppTemplate = record && ('lti_app_template' in record ? record.lti_app_template : null);

        setIsExistConnectedApp(
            appTemplates.map(e => e.uuid)
                ?.includes(ltiAppTemplate?.uuid ?? '')
        );
        setIsActiveForApp(
            !!appTemplates.find(e => e.uuid === ltiAppTemplate?.uuid)?.is_active
        );
    }, [visible, appTemplates]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (initSectionNum === 3 && isUpdate && visible) {
            setIsDisabledSubmitBtn(true);
            setFormInitValue({
                ...form.getFieldsValue(),
                duration: convertDateToString(form.getFieldValue('duration')),
            });
        }
    }, [initSectionNum]);

    useEffect(() => {
        if (isUpdate && visible && initSectionNum === 3) {
            const isFormChange = checkFormChange(formInitValue, formChangeValue);
            setIsDisabledSubmitBtn(!isFormChange);
        }
    }, [formChangeValue]);

    useEffect(() => {
        handleValuesChange(form.getFieldsValue());
    }, [autoCompleteUrl]);

    const onFinish = (values: FormValues) => {
        if (!validations.isValidUrl('url', 'URL', 1999, form, values.url)) {
            return false;
        }
        if (!validations.isValidUrl('ltiUrlUrl', 'URL', 1999, form, values.ltiUrlUrl)) {
            return false;
        }

        const postBody = createPostBody(
            category.uuid,
            record,
            values,
            applications.length,
            isCooperationUrl,
            selectAppTemplate,
            shouldCreate,
        );

        if (shouldCreate) {
            dispatch(Actions.http.connection.apps.create(postBody, category.uuid));
        } else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            dispatch(Actions.http.connection.apps.update(record!.uuid, postBody, category.uuid));
        }
    };

    const handleChangeIsUrl = (e: RadioChangeEvent) => {
        setIsCooperationUrl(e.target.value);
        if (!e.target.value) {// Appに変更した時
            form.setFieldsValue({
                url: undefined
            });
        }
        // 設定済みのアイコンの削除とプリセットを選択したフラグを削除
        fileUploaderRef.current?.removeAll?.();
        setSelectedPresetFlags(undefined);
    };

    const handleSelectAppTemplate = (appTemplateUuid: string) => {
        const newSelectAppTemplate = appTemplates.find(e => e.uuid === appTemplateUuid);
        newSelectAppTemplate && setSelectAppTemplate(newSelectAppTemplate);

        const initialValues = createInitialValues(newSelectAppTemplate, isControllableUser);
        initialValues && form.setFieldsValue(initialValues);
    };

    const handleValuesChange = (valuesChange: any) => {
        if (isUpdate) {
            setFormChangeValue({
                ...valuesChange,
                duration: convertDateToString(valuesChange.duration),
            });
        }
    };

    const handleCancel = () => {
        onCancel();
        if (isUpdate) {
            setIsDisabledSubmitBtn(false);
        }
        setIsSelectDefaultImage(false);
    };

    return (
        <BaseModal
            className='common-modal apps-modal edit-modal'
            footer={[
                <Button key='back' onClick={handleCancel} size='large'>キャンセル</Button>,
                <Button key='create'
                    disabled={disabledForPublishedItem || !isEditable || !visible || isDisabledSubmitBtn}
                    loading={searchTemplateLoading || editLoading}
                    onClick={() => form.submit()}
                    size='large'
                    type='primary'
                >
                    保存
                </Button>,
            ]}
            forceRender
            maskClosable={false}
            onCancel={handleCancel}
            style={{ top: 20, width: '97vw !important' }}
            title={<span>{`教材・アプリの${shouldCreate ? '新規作成' : '編集'}`}</span>}
            visible={visible}
        >
            <Spin spinning={detailLoading}>
                {(!disabledForPublishedItem && !isEditable) ? (
                    <Alert
                        message={isExistConnectedApp ? '配信アプリが無効化されているため編集できません' : '削除されている・もしくは配信対象校でないため編集できません'}
                        showIcon
                        style={{ marginBottom: '1rem' }}
                        type='error'
                    />
                ) : null}

                <PublishedItemMessage disabledForPublishedItem={disabledForPublishedItem} />

                <Form {...modalBaseLayout} form={form} name='control-hooks-edit-apps' onFinish={onFinish}
                    onValuesChange={(_, allValues) => handleValuesChange(allValues)}>
                    <Form.Item name='title'
                        label='タイトル'
                        rules={[
                            formRules.required({ label: '教材・アプリ名称' }),
                            formRules.range({ label: '教材・アプリ名称', max: 128 }),
                        ]}
                    >
                        <Input
                            disabled={disabledForPublishedItem || !isEditable}
                            placeholder='教材・アプリ名称を入力してください'
                        />
                    </Form.Item>

                    <Form.Item name='description'
                        label='概要'
                        rules={[formRules.range({ label: '概要', max: 128 })]}
                    >
                        <Input
                            disabled={disabledForPublishedItem || !isEditable}
                            placeholder='概要を入力してください'
                        />
                    </Form.Item>

                    <Form.Item name='isCooperationUrl'
                        label='連携方式'
                        rules={[{ message: '必須項目です', required: true }]}
                    >
                        <Radio.Group
                            disabled={disabledForPublishedItem || isUpdate || !isEditable}
                            onChange={handleChangeIsUrl}
                        >
                            <Radio value={true}>URL</Radio>
                            <Radio value={false}>アプリ配信</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/*連携方式がアプリの場合*/}
                    {!isCooperationUrl ? (
                        <>
                            <Form.Item name='control_application_template_uuid'
                                {...modalBaseLayoutWithoutLabel}
                                rules={[{ message: '必須項目です', required: true }]}
                            >
                                <Select
                                    disabled={disabledForPublishedItem || isUpdate || !isEditable}
                                    onChange={handleSelectAppTemplate}
                                    placeholder='利用する配信アプリを選択してください'
                                    value={selectAppTemplate?.uuid}
                                >
                                    {appTemplates.filter(elm => elm.is_active).map((appTemplate, index) => (
                                        <Option key={index} value={appTemplate.uuid}>{appTemplate.name}</Option>)
                                    )}
                                </Select>
                            </Form.Item>

                            <Form.Item wrapperCol={{ lg: { offset: 0, span: 24 }, xl: { offset: 0, span: 24 } }}>
                                <EditUrlAndParam
                                    appTemplate={selectAppTemplate || record}
                                    context='App'
                                    disabled={disabledForPublishedItem || !isEditable}
                                    isControllableUser={isControllableUser}
                                    isCreate={isCreate}
                                    layout={modalBaseLayout}
                                    layoutWithoutLabel={modalBaseLayoutWithoutLabel}
                                />
                            </Form.Item>
                        </>
                    ) : null}

                    {/*連携方式がURLの場合*/}
                    {isCooperationUrl ? (
                        <Form.Item name='url'
                            label='URL'
                            rules={[
                                formRules.required({ label: '遷移先URL' }),
                                formRules.range({ label: '遷移先URL', max: 1999 }),
                            ]}
                        >
                            <AutoCompleteUrl
                                addFiles={(file) => fileUploaderRef.current?.add?.(file)}
                                buttonDisable={disabledForPublishedItem || !isEditable}
                                disabled={disabledForPublishedItem || !isEditable || !!selectedPresetFlags?.url || useDeepLinking}
                                form={form}
                                name='url'
                                onReset={() => setSelectedPresetFlags({ files: false, url: false })}
                                onSelect={() => {
                                    setSelectedPresetFlags({ files: true, url: true })
                                    setIsSelectDefaultImage(false)
                                }}
                                isSelectDefaultImage={isSelectDefaultImage}
                            />
                        </Form.Item>
                    ) : null}

                    <Form.Item name='open_new_window'
                        label={
                            <span>
                                新規タブ/ウィンドウ設定
                                <Tooltip title='クリック時に新規タブで開くか現在のタブで開くかを選択できます'>
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Radio.Group {...modalBaseLayout}
                            disabled={!isEditable}
                        >
                            <Radio
                                disabled={disabledForPublishedItem}
                                style={{ display: 'block', height: '30px', lineHeight: '30px' }}
                                value={true}
                            >
                                新規タブ/ウィンドウで開く
                            </Radio>
                            <Radio
                                disabled={disabledForPublishedItem}
                                style={{ display: 'block', height: '30px', lineHeight: '30px' }}
                                value={false}
                            >
                                現在のタブ/ウィンドウで開く
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name='duration'
                        label='公開期間'
                        rules={[formRules.requiredDuration({ label: '公開期間' })]}
                    >
                        <CustomRangePicker name='duration'
                            disabled={disabledForPublishedItem || !isEditable}
                            disabledDate={getDisabledDate}
                            form={form}
                            format='YYYY/MM/DD HH:mm'
                            locale={jaJP}
                            ranges={DatePickerRangesIncludeInfinite()}
                            showTime={{ format: 'HH:mm' }}
                        />
                    </Form.Item>

                    <Form.Item name='files'
                        label={
                            <span>
                                アイコン画像
                                {disabledForPublishedItem || !isEditable ? (
                                    <Button type='link' disabled>
                                        アイコンの候補
                                    </Button>
                                ) : (
                                    <Popover
                                        content={
                                            <IconCandidate
                                                form={form}
                                                addFiles={(file) => fileUploaderRef.current?.add?.(file)}
                                                handleSelect={() =>
                                                    !isSelectDefaultImage && setIsSelectDefaultImage(true)
                                                }
                                            />
                                        }
                                        title='アイコンの候補'
                                        trigger='click'>
                                        <Button type='link'>アイコンの候補</Button>
                                    </Popover>
                                )}
                            </span>
                        }>
                        <FileUploader
                            allowedExtension={allowedExtensions.image}
                            disabled={
                                disabledForPublishedItem ||
                                !isEditable ||
                                !!selectedPresetFlags?.files ||
                                isAutoCompleteUrl
                            }
                            ref={fileUploaderRef}
                        />
                    </Form.Item>
                    {/* テナントの多要素認証設定が無効の場合は非表示（hidden なので値は保持したまま送信される） */}
                    <Form.Item
                        name='enableTwoFactorAuth'
                        label='多要素認証を要求する'
                        tooltip='多要素認証が有効になっているテナントのみ表示'
                        hidden={!isEnableTenantMFA}
                    >
                        <Radio.Group disabled={disabledForPublishedItem || !isEditable}>
                            <Radio value={true}>する</Radio>
                            <Radio value={false}>しない</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
                <hr />
            </Spin>
        </BaseModal>
    );
};

/***
 * 全体管理者から配信されたアプリでのメッセージ
 */
const PublishedItemMessage: React.FC<{ disabledForPublishedItem: boolean }> = ({ disabledForPublishedItem }) => {
    if (!disabledForPublishedItem) return null;

    return (
        <div className='mb-8'>
            <Alert message={<span>全体管理者で管理されています。閲覧のみ可能です。</span>} showIcon type='warning' />
        </div>
    );
};

const convertUrlToSubmittable = (selectAppTemplate: AppTemplate, values: FormValues, uuidKeyName: string) => {
    const url = selectAppTemplate.url;

    if (selectAppTemplate.type === 1) {
        const { ltiUrlCandidateUuid, ltiUrlUrl, ltiUrlKey, ltiUrlSecret } = values;
        // TODO: TypeScript 5.2.2 でキャストする必要がなくなる
        const candidate = (url.candidates as (typeof url.candidates)[0][]).find(c => c?.uuid === ltiUrlCandidateUuid);
        return {
            key: candidate ? candidate.key : ltiUrlKey,
            secret: candidate ? candidate.secret : ltiUrlSecret,
            url: candidate ? candidate.url : ltiUrlUrl,
            [uuidKeyName]: url.uuid,
        };
    } else if (selectAppTemplate.type === 2) {
        if (url.is_choices) {
            const { ltiUrlCandidateUuid } = values;
            const candidate = ltiUrlCandidateUuid ?
                // TODO: TypeScript 5.2.2 でキャストする必要がなくなる
                (url.candidates as (typeof url.candidates)[0][]).find(c => c?.uuid === ltiUrlCandidateUuid) :
                (url.candidates as (typeof url.candidates)[0][]).find((c: NonNullable<typeof url.candidates>[0]) => c.is_default);
            return {
                param_json: candidate?.param_json,
                [uuidKeyName]: url.uuid,
            };
        } else {
            return {
                param_json: values.param_json,
                [uuidKeyName]: url.uuid,
            };
        }
    }
};

const convertParamToSubmittable = (editableParams: AppTemplate['params'], paramValue: FormParams, uuidKeyName: string) => {
    const { ltiParamUuid, ltiParamCandidateUuid, ltiParamValue } = paramValue;
    const param = editableParams?.find(v => v.uuid === ltiParamUuid);
    const candidate = ltiParamCandidateUuid &&
        param?.candidates?.find((c: NonNullable<typeof param['candidates']>[0]) => c?.uuid === ltiParamCandidateUuid);

    return {
        key: param?.key ?? '',
        [uuidKeyName]: ltiParamUuid,
        value: candidate ? candidate.value : ltiParamValue,
    };
};

/***
 * 学校管理者で保存済みの値と、アプリテンプレートを統合する
 */
const getAppTemplateWithDefaultValue = (newRecord: App): AppTemplate | null => {
    if (!('lti_app_template' in newRecord) || !newRecord.lti_app_template) return null;

    return Object.assign(
        {},
        newRecord.lti_app_template,
        {
            lti_app_template: newRecord.lti_app_template,
            // TypeScript のエラーになるので、一旦展開している
            params: [...newRecord.lti_app_template.params].map((tempParam) => {
                const paramWithDefaultValue = newRecord.params?.find(param => param.key === tempParam.key) ?? {};
                return Object.assign({}, tempParam, paramWithDefaultValue);
            }),
            url: Object.assign({}, newRecord.lti_app_template.url, newRecord.lti_url),
            // AppTemplate と App のもつ app_template の型が異なるので、一旦 any で対応
        } as any
    );
};

const createPostBody = (
    categoryUuid: string,
    record: App | null,
    formValues: FormValues,
    newOrder: number,
    isCooperationUrl: boolean,
    selectAppTemplate: AppTemplate | null,
    shouldCreate: boolean,
): Http.Connection.Request.Parameter.App.Create | undefined => {
    const postBody = {
        category_uuid: categoryUuid,
        description: formValues.description,
        files: formValues.files.map(file => file?.uuid ?? file.uid).filter(e => e),
        open_new_window: formValues.open_new_window,
        order: record && record.order !== null ? record.order : newOrder,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        publish_end_at: formValues.duration[1]!.unix(),
        publish_start_at: formValues.duration[0].unix(),
        title: formValues.title,
        enable_two_factor_auth: formValues.enableTwoFactorAuth,
    };

    if (isCooperationUrl) {
        // URL連携
        return {
            ...postBody,
            url: formValues.url,
        };
    } else {
        if (selectAppTemplate === null) {
            throw new Error('AppTemplate is null.');
        }
        // アプリ連携
        const urlUuidKeyName = shouldCreate ? 'control_application_url_uuid' : 'uuid';
        const paramUuidKeyName = shouldCreate ? 'control_application_param_uuid' : 'uuid';

        return {
            ...postBody,
            control_application_template_uuid: formValues.control_application_template_uuid ?? '',
            lti_url: convertUrlToSubmittable(selectAppTemplate, formValues, urlUuidKeyName),
            params: formValues.params && Array.isArray(formValues.params) && selectAppTemplate ?
                formValues.params.map(param =>
                    convertParamToSubmittable(selectAppTemplate.params, param, paramUuidKeyName)
                ) :
                undefined,
        };
    }
};

export default EditApp;
